/**
 * Утилиты для работы с JWT токенами
 */

export interface JWTPayload {
    exp: number;
    iat?: number;
    user_id?: number;
    employee_id?: number;
    username?: string;
    email?: string;
    roles?: string[];
    permissions?: string[];
    [key: string]: unknown;
}

/**
 * Декодирует JWT токен без проверки подписи
 * @param token - JWT токен
 * @returns Декодированный payload или null если токен невалидный
 */
export function decodeJWT(token: string): JWTPayload | null {
    if (!token || !token.includes('.')) {
        return null;
    }
    try {
        const parts = token.split('.');
        if (parts.length !== 3) {
            return null;
        }

        const payload = parts[1];
        const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
        return JSON.parse(decoded) as JWTPayload;
    } catch (error) {
        console.error('[decodeJWT] Failed to decode token:', error);
        return null;
    }
}

/**
 * Проверяет, истек ли токен
 * @param token - JWT токен
 * @param bufferSeconds - Буфер времени в секундах (по умолчанию 60 секунд)
 * @returns true если токен истек или будет истекать в течение bufferSeconds
 */
export function isTokenExpired(token: string | null, bufferSeconds = 60): boolean {
    if (!token) {
        return true;
    }

    // ✅ ИСПРАВЛЕНИЕ: dev-local-token никогда не истекает
    if (token === 'dev-local-token' || token === 'dev-token') {
        return false;
    }

    const payload = decodeJWT(token);
    if (!payload || !payload.exp) {
        // Если это не JWT, но мы его допустили (например, другой статический токен),
        // считаем его неистекшим, если мы в режиме разработки
        return !import.meta.env.DEV;
    }

    const now = Math.floor(Date.now() / 1000);
    const expirationTime = payload.exp;

    // Токен истек или истечет в течение bufferSeconds
    return expirationTime <= now + bufferSeconds;
}

/**
 * Получает время истечения токена
 * @param token - JWT токен
 * @returns Время истечения в миллисекундах или null
 */
export function getTokenExpirationTime(token: string | null): number | null {
    if (!token) {
        return null;
    }

    const payload = decodeJWT(token);
    if (!payload || !payload.exp) {
        return null;
    }

    return payload.exp * 1000; // Конвертируем в миллисекунды
}

/**
 * Получает оставшееся время жизни токена в секундах
 * @param token - JWT токен
 * @returns Оставшееся время в секундах или 0 если токен истек
 */
export function getTokenTimeToExpiry(token: string | null): number {
    if (!token) {
        return 0;
    }

    const expirationTime = getTokenExpirationTime(token);
    if (!expirationTime) {
        return 0;
    }

    const now = Date.now();
    const remaining = Math.floor((expirationTime - now) / 1000);

    return Math.max(0, remaining);
}

